import { resetLocalAuthKey } from '@/common/auth';
import { ContentPanel } from '@/components/content-panel';
import { useApolloClient } from '@apollo/client';
import { Alert, Text } from '@mantine/core';
import { IconAlertCircle, IconX } from '@tabler/icons-react';
import { useNavigate } from '@tanstack/react-router';
import { FormikProps } from 'formik';
import { useCallback, useMemo, useRef } from 'react';
import { CompactButton } from '../../components/buttons/compact';
import { StandardButton } from '../../components/buttons/standard';
import { PrimaryLayout } from '../../layouts/primary';
import { DeleteAccountForm, DeleteAccountValues } from './form';
import { useDeleteAccountMutation } from './graphql.generated';

export function DeleteAccountPage() {
	const navigate = useNavigate();
	const client = useApolloClient();
	const [deleteAccount, { loading, error }] = useDeleteAccountMutation();

	const formRef = useRef<FormikProps<DeleteAccountValues>>(null);

	const handleDeleteAccountClick = useCallback(() => {
		formRef.current?.submitForm();
	}, []);

	const onSubmit = useCallback(
		async (values: DeleteAccountValues) => {
			await deleteAccount({
				variables: {
					input: {
						reason: values.reasonForDeletion,
						comments: values.comments,
					},
				},
			});
			resetLocalAuthKey();
			client.clearStore();
			navigate({ to: '/welcome' });
		},
		[deleteAccount, client, navigate]
	);

	const closeButton = useMemo(
		() => (
			<CompactButton leftSection={<IconX />} onClick={() => navigate({ to: '/' })}>
				Close
			</CompactButton>
		),
		[navigate]
	);

	const deleteAccountButton = useMemo(
		() => (
			<StandardButton
				variant="filled"
				color="red"
				onClick={handleDeleteAccountClick}
				disabled={loading}
			>
				{loading ? 'Deleting Account...' : 'Delete Account'}
			</StandardButton>
		),
		[loading, handleDeleteAccountClick]
	);

	return (
		<PrimaryLayout
			header={{ title: 'Delete Account', children: closeButton }}
			footer={{ children: deleteAccountButton }}
		>
			<ContentPanel>
				<Alert icon={<IconAlertCircle />} variant="filled" bg="red.0" mb="md">
					<Text size="sm">Account deletion is permanent and cannot be undone once confirmed.</Text>
				</Alert>
				<DeleteAccountForm onSubmit={onSubmit} formRef={formRef} />
				{error && (
					<Alert icon={<IconAlertCircle />} variant="filled" bg="red.0" mt="xl">
						<Text size="sm">
							An error occurred while deleting your account. Please try again later.
						</Text>
					</Alert>
				)}
			</ContentPanel>
		</PrimaryLayout>
	);
}
